import React, { useMemo } from 'react'
import styled from 'styled-components'
import SlideBase from './SlideBase'
import WrappedImageBackground from '../WrappedImageBackground'
import Caption from 'LuxKit/Typography/Caption'
import AssistChip from 'LuxKit/Chips/AssistChip'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Sheet from 'components/utils/Sheet'
import FadeBackground from '../FadeBackground'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import pluralize from 'lib/string/pluralize'
import { AVERAGE_TRAVELLER_TEXT } from '../../config/averages'
import Flag from 'LuxKit/Flag'

const ChipsContainer = styled(Sheet)`
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
  -webkit-mask-image: linear-gradient(to bottom, black 90%, transparent 100%);
`

const BottomContent = styled.div`
  flex-shrink: 0;
`

const BACKGROUND_FALLBACK_IMAGEID = 'wrap-travelled-backup'
const SLIDE_ID = 'destinations' as const

function getMemoriesHeading(isSingleCity: boolean, isMultiCity: boolean): string {
  if (isSingleCity) return 'You made memories in'
  if (isMultiCity) return 'You made memories across'
  return 'On average, our travellers made memories across'
}

function DestinationsSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const isPaused = useWrappedStorySelector(state => state.isPaused)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)

  const stats = data?.stats
  const citiesCount = stats?.citiesVisited.length ?? 0
  const countriesCount = stats?.countriesVisited ?? 0
  const firstCity = stats?.citiesVisited[0]
  const isSingleCity = citiesCount === 1
  const isMultiCity = !isSingleCity && !!citiesCount
  const notTravelled = !citiesCount
  const backgroundImage = firstCity?.imageId ?? BACKGROUND_FALLBACK_IMAGEID
  const memoriesHeading = getMemoriesHeading(isSingleCity, isMultiCity)
  const countryText = pluralize({ singular: 'country', plural: 'countries', count: countriesCount }).text
  const cityText = pluralize({ singular: 'city', plural: 'cities', count: citiesCount }).text

  // Memoize chips to prevent recreation
  const chipsOverlay = useMemo(() => {
    if (isSingleCity) return undefined
    return (
      <ChipsContainer paletteType="default">
        <Group direction="vertical" horizontalAlign="center" gap={8}>
          {stats?.citiesVisited.map((city, index) => (
            <AssistChip
              key={`${city.name}-${city.country}-${index}`}
              variant="filled"
              size="large"
            >
              <Flag countryCode={city.countryCode} /> {city.name}
            </AssistChip>
          ))}
        </Group>
      </ChipsContainer>
    )
  }, [isSingleCity, stats?.citiesVisited])

  // Memoize background to prevent recreation
  const background = useMemo(() => {
    if (!backgroundImage) return undefined
    return (
      <WrappedImageBackground
        imageId={backgroundImage}
        alt={firstCity?.name}
        kenBurnsVariant="left-to-right"
        isActive={isCurrentSlide && !isPaused}
      />
    )
  }, [backgroundImage, firstCity?.name, isCurrentSlide, isPaused])

  if (!data) return null

  return (
    <SlideBase
      verticalAlign={chipsOverlay ? 'space-between' : 'end'}
      background={background}
    >
      {chipsOverlay}
      <BottomContent>
        <FadeBackground>
          <Group direction="vertical" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">
                Destinations visited
              </Caption>
              <Heading variant="heading5">
                <b>{memoriesHeading}</b>
              </Heading>
            </Group>
            {isSingleCity && (
              <Heading variant="heading1">
                <i>{firstCity?.name}</i>
              </Heading>
            )}
            {isMultiCity && (
              <Heading variant="heading1">
                <i>
                  {citiesCount} {cityText}<br />
                  in {countriesCount} {countryText}
                </i>
              </Heading>
            )}
            {notTravelled && (
              <Heading variant="heading1">
                <i>
                  {AVERAGE_TRAVELLER_TEXT}
                </i>
              </Heading>
            )}
          </Group>
        </FadeBackground>
      </BottomContent>
    </SlideBase>
  )
}

export default React.memo(DestinationsSlide)
